<template>
  <div class="mt-4" data-cy="conflict-log">
    <div class="flex flex-wrap items-end gap-4">
      <div class="w-44">
        <LlDatePicker
          v-model="filterState.from"
          label="Run from"
          :max="filterState.to || undefined"
          data-cy="conflict-log-from"
          @update:model-value="applyDates"
        />
      </div>
      <div class="w-44">
        <LlDatePicker
          v-model="filterState.to"
          label="Run to"
          :min="filterState.from || undefined"
          data-cy="conflict-log-to"
          @update:model-value="applyDates"
        />
      </div>
      <LlSecondaryButton
        v-if="recordStore.isFiltered"
        size="small"
        class="mb-1"
        data-cy="conflict-log-clear-dates"
        @click="clearDates"
      >
        Clear dates
      </LlSecondaryButton>
    </div>

    <v-skeleton-loader
      v-if="recordStore.logLoading && !recordStore.items.length"
      class="mt-4"
      type="table-row-divider@5"
      data-cy="conflict-log-loading"
    />

    <!-- An error is never shown as an empty log: "none saved" and "could not load"
         would send a reviewer to opposite conclusions. -->
    <div
      v-else-if="recordStore.logError"
      class="mt-4 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed"
      data-cy="conflict-log-error"
    >
      <span class="font-medium text-red-700"
        >Previous checks could not be loaded.</span
      >
      This is not an empty list — try again.
      <div class="mt-2.5">
        <LlSecondaryButton size="small" @click="load"
          >Try again</LlSecondaryButton
        >
      </div>
    </div>

    <div
      v-else-if="!recordStore.items.length"
      class="mt-4 rounded border border-gray-200 bg-white px-3.5 py-3 text-xs leading-relaxed"
      data-cy="conflict-log-empty"
    >
      <template v-if="recordStore.isFiltered">
        <span class="font-medium">No checks were saved in these dates.</span>
        Widen the dates or clear them to see every saved check.
      </template>
      <template v-else>
        <span class="font-medium">No saved checks yet.</span>
        When anyone at the firm saves a check, it appears here with who ran it
        and when.
        <div class="mt-2.5">
          <LlSecondaryButton size="small" @click="emit('new')">
            Run a check
          </LlSecondaryButton>
        </div>
      </template>
    </div>

    <template v-else>
      <LlDataTable
        class="mt-4 border border-gray-200"
        :headers="headers"
        :items="recordStore.items"
        :items-length="recordStore.total"
        :loading="recordStore.logLoading"
        :must-sort="false"
        data-cy="conflict-log-table"
      >
        <template #[`item.runAtUtc`]="{ item }">
          <a
            :href="checkHref(item.id) ?? undefined"
            class="font-medium text-primary underline-offset-2 hover:underline"
            data-cy="conflict-log-open"
            @click.prevent="emit('open', item.id)"
          >
            {{ formatRunAt(item.runAtUtc) }}
          </a>
        </template>
        <template #[`item.parties`]="{ item }">
          <span class="[overflow-wrap:anywhere]">
            {{ item.parties.join(' · ') }}
          </span>
        </template>
        <template #[`item.counts`]="{ item }">
          <span v-if="!item.fullCount && !item.partialCount">
            No conflicts found
          </span>
          <span v-else class="whitespace-nowrap">
            {{ item.fullCount }} full · {{ item.partialCount }} partial
          </span>
        </template>
        <template #[`item.hasNotes`]="{ item }">
          {{ item.hasNotes ? 'Yes' : 'None' }}
        </template>
        <template #[`item.includedTimeEntries`]="{ item }">
          {{ item.includedTimeEntries ? 'Included' : 'Not searched' }}
        </template>
      </LlDataTable>
      <LlDataTablePagination
        :page="recordStore.filter.page"
        :page-size="recordStore.filter.pageSize"
        :total-count="recordStore.total"
        :page-size-options="PAGE_SIZE_OPTIONS"
        @update:page="changePage"
        @update:page-size="changePageSize"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { parseLocalDate } from '@/services/utils';
import LlDataTable from '@/components/LlDataTable.vue';
import LlDataTablePagination from '@/components/LlDataTablePagination.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { useConflictCheckRecordStore } from '../stores/useConflictCheckRecordStore';
import { formatRunAt, toApiDate } from '../utils/conflictDateHelpers';

defineProps<{ checkHref: (id: string) => string | null }>();

const emit = defineEmits<{ open: [id: string]; new: [] }>();

/** The API caps pageSize at 100. */
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const headers = [
  { title: 'Run', key: 'runAtUtc', sortable: false },
  { title: 'Run by', key: 'runByName', sortable: false },
  { title: 'Names checked', key: 'parties', sortable: false },
  { title: 'Results', key: 'counts', sortable: false },
  { title: 'Notes', key: 'hasNotes', sortable: false },
  { title: 'Time entries', key: 'includedTimeEntries', sortable: false },
];

const recordStore = useConflictCheckRecordStore();
const { handleError } = useSnackbarHandler();

const filterState = reactive<{ from: Date | null; to: Date | null }>({
  from: parseLocalDate(recordStore.filter.from),
  to: parseLocalDate(recordStore.filter.to),
});

async function load(): Promise<void> {
  try {
    await recordStore.loadLog();
  } catch (error) {
    handleError(error);
  }
}

function applyDates(): void {
  const from = toApiDate(filterState.from);
  const to = toApiDate(filterState.to);
  if (from === recordStore.filter.from && to === recordStore.filter.to) return;
  recordStore.setDateRange(from, to);
  load();
}

function clearDates(): void {
  filterState.from = null;
  filterState.to = null;
  applyDates();
}

function changePage(page: number): void {
  recordStore.setPage(page);
  load();
}

function changePageSize(pageSize: number): void {
  recordStore.setPageSize(pageSize);
  load();
}

onMounted(load);
</script>
